import { AxiosInstance } from 'axios';
import { ErrorHandlerParams, ReplayRequest, RestErrorMessages } from 'core/models/httpClient';
import { openToastr, openAlertDialog, openSessionDialog } from 'src/_common/handlers/services';
import Bugsnag from '@bugsnag/js';
import { getAuth } from 'firebase/auth';
import Firestore from 'src/authentication/handlers/firestore';
import { getReduxStore } from 'src/redux/index';
import { RootState } from '@Common/models/redux/store';
import CaptchaLimitHandler from './captchaLimitHandler';
import networkGateway from './gateway';
import Recaptcha from '../recaptchaClient';
import { translateText } from '@Common/handlers/helper';
import Track from '@Common/handlers/track';

let axiosInstance: AxiosInstance;

const replayRequest = (userRequest: ReplayRequest) =>
  new Promise((resolve, reject) => {
    switch (userRequest.method) {
      case 'POST' || 'post':
        networkGateway((useridtoken) => {
          axiosInstance
            .post(userRequest.url, userRequest.data, {
              headers: {
                useridtoken,
              },
            })
            .then((succ) => resolve(succ))
            .catch((err) => reject(err));
        });
        break;

      case 'GET' || 'get':
        networkGateway((useridtoken) => {
          axiosInstance
            .get(userRequest.url, {
              params: userRequest.data,
              headers: {
                useridtoken,
              },
            })
            .then((succ) => resolve(succ))
            .catch((err) => reject(err));
        });
    }
  });

const handleRecaptcha = (userRequest: ReplayRequest, sendToken: boolean) =>
  new Promise((resolve, reject) => {
    // Don't remove this code. Will need to test and verify if everything works fine without this code or not.
    // const url =
    //   userRequest.method === 'GET' ? `${userRequest.url}?${userRequest.data}` : userRequest.url;
    // let error: Object = {};
    // Now we should not need this CaptchaLimitHandler as we will reject if we get error after replay request.
    // If there is an error in replay request.. if it's 408, it's our implmentation issue. For other cases, relevant msg shall be shown.
    // if (
    //   !CaptchaLimitHandler.getInstance().getUsage(url) ||
    //   CaptchaLimitHandler.getInstance().getUsage(url) < 2
    // ) {
    //   CaptchaLimitHandler.getInstance().addUsage(url);
    Recaptcha.getInstance().recaptchaEventEmitter(
      () =>
        replayRequest(userRequest).then(
          (res) => resolve(res),
          (err) => {
            reject(err);
            Track.replayRequestFailure({
              url: userRequest.url,
              errorCode: err?.code,
              message: err?.message,
              trace: err?.traceID,
            });
            openToastr('Something went wrong, please refresh the page');
            Bugsnag.notify({
              name: 'Recaptcha Process Failed',
              message: `Got Error after replaying request. Error Code: ${err?.code}`,
              errorClass: err?.toString(),
            });
            // handleRecaptcha(userRequest);
          }
        ),
      true,
      sendToken
    );
    // }
    // else {
    // Bugsnag.notify({
    //   name: 'Recaptcha',
    //   message: ' Recaptcha called thrice',
    //   errorClass: error.toString(),
    // });
    // This has been handled in the server, DO NOT REMOVE
    // QuillEventEmitter.getInstance().emit('RENDER_RECAPTCHA_PUZZLE_EVENT', {
    //   cb: () =>
    //     replayRequest(userRequest).then(
    //       (res) => resolve(res),
    //       () => {
    //         CaptchaLimitHandler.getInstance().reset(userRequest.url);
    //         logger('INCORRECT BEHAVIOR FROM REST SERVER', '');
    //         reject({
    //           code: 408,
    //           data: {},
    //           message: 'Recaptcha Verification Failed',
    //           status: 408,
    //           traceID: 'NA',
    //         });
    //       }
    //     ),
    // });
    // reduxStore.dispatch(
    //   toggleRecaptchaAction({
    //     isShown: true,
    //   })
    // );
    // }
  });

const forceShowRecaptcha = () =>
  new Promise((_, reject) => {
    Recaptcha.getInstance().recaptchaEventEmitter((token: string) => {
      const rejectRequest = (err: { code: string }) => {
        reject(err);
        openToastr('Something went wrong, please refresh the page');
        Bugsnag.notify({
          name: 'Recaptcha Process Failed',
          message: `Got Error after replaying request. Error Code: ${err?.code}`,
          errorClass: err?.toString(),
        });
      };

      networkGateway((useridtoken?: string) => {
        axiosInstance({
          method: 'POST',
          url: 'payments/spam-check',
          data: { token },
          headers: {
            useridtoken,
          },
        })
          .then(() => {
            openToastr('Please click on Checkout to continue payment');
            reject();
          })
          .catch((err) => rejectRequest(err));
      });
    }, false);
  });

let maintDialogTimer: NodeJS.Timeout;
export const handleMaintenanceDialog = () => {
  const isConnected = !!(getReduxStore()?.getState() as RootState)?.common?.maintenance?.globalMaintenance.message;
  if (isConnected) {
    openAlertDialog(
      true,
      (getReduxStore()?.getState() as RootState)?.common?.maintenance?.alertTitle || 'QuillBot Under Maintenance',
      (getReduxStore()?.getState() as RootState)?.common?.maintenance?.alertMessage ||
        'QuillBot is currently down for maintenance, check back soon.'
    );
    if (maintDialogTimer) {
      clearInterval(maintDialogTimer);
    }
  } else if (!maintDialogTimer) {
    maintDialogTimer = setInterval(() => {
      handleMaintenanceDialog();
    }, 500);
  }
};

const ErrorHandler = (params: ErrorHandlerParams) =>
  new Promise((resolve, reject) => {
    axiosInstance = params.axiosInstance;

    if (!params.serverError || !params.serverError.status) {
      reject({
        code: 408,
        data: {},
        message: 'Recaptcha Verification Failed',
        status: 408,
        traceID: 'NA',
      });
      return;
    }

    const errorCode = params.serverError.code;
    switch (params.serverError.status) {
      case 403:
        if (getAuth().currentUser) {
          if (errorCode === RestErrorMessages.USER_PREMIUM_FORBIDDEN) {
            Firestore.getInstance().updateDetails(2, undefined, true);
          } else if (params.replayRequest?.url.includes('restore-project')) {
            // ! Warning: Dont remove this code. This is being handle in catch block
            // openToastr('Requested project not found! Please select a valid project.');
          } else if (errorCode === RestErrorMessages.COM_STORAGE) {
            // ! Warning: Dont remove this code. This is being handle in catch block
            // openToastr('Something went wrong, please refresh the page');
          } else {
            openToastr('Something seems off, Please Logoff & Login again.');
            Bugsnag.notify({ name: 'AUTH_ERROR', message: 'User thrown 403' });
          }
        } else {
          openToastr('Either you are not logged in, or something seems off, please refresh the page');
          Bugsnag.notify({
            name: 'AUTH_ERROR',
            message: 'Not logged in user thrown 403',
          });
        }
        reject(errorCode);
        break;

      case 408:
        if (params.replayRequest?.url.includes('detect-location')) reject(params.serverError);
        else {
          handleRecaptcha(params.replayRequest, false).then(
            (res) => {
              CaptchaLimitHandler.getInstance().reset(params.replayRequest.url);
              resolve(res);
            },
            (err) => reject(err)
          );
        }
        break;

      case 429:
        if (params.replayRequest?.url.includes('predict-next-text')) {
          reject(params.serverError);
        } else {
          openAlertDialog(
            false,
            translateText('Suspicious Activity Notice'),
            [
              translateText(
                'Our systems have detected an excessive sentence processing rate. Please wait for 10 mins before trying again.'
              ),
              translateText(
                'Please don’t share your log in credentials with others as this may be the cause to increased sentence processing.'
              ),
            ],
            translateText('I understand')
          );
          reject(params.serverError);
        }
        break;

      case 701:
        openSessionDialog();
        reject(params.serverError);
        break;

      case 702:
        // circuit breaker is just applied for single paraphrase and single flip currently
        if (params.replayRequest.url !== '/paraphraser/single-flip') {
          openToastr('Something seems off, our systems will be back online in a moment', 10000);
        }
        reject(params.serverError);
        break;

      case 703:
        forceShowRecaptcha().catch(() => reject(params.serverError));
        break;

      case 704:
        openToastr('Something went wrong, please refresh the page');
        reject(params.serverError);
        break;

      case 705:
        handleRecaptcha(params.replayRequest, true).then(
          (res) => {
            CaptchaLimitHandler.getInstance().reset(params.replayRequest.url);
            resolve(res);
          },
          (err) => reject(err)
        );
        break;

      case 706:
        openToastr('You might have older version, Please refresh the page.', 10000);
        reject(params.serverError);
        break;

      case 712:
        handleMaintenanceDialog();
        reject(params.serverError);
        break;

      case 716:
        openToastr("Another scan is in progress. Please try again once it's completed.", 10000);
        reject(params.serverError);
        break;

      case 720:
        Firestore.getInstance().completeSignOut();
        reject(params.serverError);
        break;

      default:
        reject(params.serverError);
    }
  });

export default ErrorHandler;
export { ErrorHandler };
